<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DocumentTypePermissionsMatrix from './DocumentTypePermissionsMatrix.vue';
import { useDocumentTypesStore } from '@/stores/documentTypes.js';
import { useRbacStore } from '@/stores/rbac.js';

const props = defineProps({
  visible: Boolean,
  itemType: {
    type: Object,
    default: null
  },
  entityType: {
    type: String,
    required: true,
    validator: (value) => ['document', 'file'].includes(value)
  }
});

const emit = defineEmits(['update:visible', 'save', 'close']);

const documentTypesStore = useDocumentTypesStore();
const rbacStore = useRbacStore();

const matrixRef = ref(null);
const loading = ref(false);
const updatedRoleIds = ref([]);
const forWork = ref(false);
const forContract = ref(false);
const forObject = ref(false);

const dialogTitle = computed(() => {
  const typeName = props.entityType === 'document' ? 'Документа' : 'Файла';
  return `Права доступа для типа ${typeName}: ${props.itemType?.item_name || props.itemType?.name || ''}`;
});

watch(() => props.visible, async (newVal) => {
  if (newVal) {
    loading.value = true;
    try {
      // Загружаем отделы и позиции если еще не загружены
      if (rbacStore.departments.length === 0) {
        await rbacStore.loadDepartments();
      }
      if (rbacStore.positions.length === 0) {
        await rbacStore.loadPositions();
      }

      // Инициализируем права и чекбоксы
      updatedRoleIds.value = props.itemType?.role_ids || [];
      forWork.value = props.itemType?.for_work ?? false;
      forContract.value = props.itemType?.for_contract ?? false;
      forObject.value = props.itemType?.for_object ?? false;
    } catch (error) {
      console.error('Ошибка загрузки данных прав:', error);
      updatedRoleIds.value = props.itemType?.role_ids || [];
    } finally {
      loading.value = false;
    }
  }
});

function handlePermissionsUpdate(roleIds) {
  updatedRoleIds.value = roleIds;
}

async function handleSave() {
  if (!props.itemType?.id) return;

  try {
    loading.value = true;
    await documentTypesStore.updateItemTypePermissions(
      props.entityType,
      props.itemType.id,
      updatedRoleIds.value,
      {
        for_work: forWork.value,
        for_contract: forContract.value,
        for_object: forObject.value
      }
    );
    emit('save');
    handleClose();
  } catch (error) {
    console.error('Ошибка сохранения прав:', error);
    throw error;
  } finally {
    loading.value = false;
  }
}

function handleClose() {
  emit('update:visible', false);
  emit('close');
}
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="dialogTitle"
    modal
    :style="{ width: '900px' }"
  >
    <div v-if="loading" class="text-center py-8">
      <i class="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
      <p class="mt-4 text-gray-600">Загрузка прав доступа...</p>
    </div>

    <div v-else>
      <DocumentTypePermissionsMatrix
        ref="matrixRef"
        :item-type="itemType"
        :entity-type="entityType"
        @update:permissions="handlePermissionsUpdate"
      />

      <!-- Чекбоксы типов сущностей -->
      <div class="mt-6 pt-4 border-t border-surface-200 dark:border-surface-700">
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-2">
            <Checkbox v-model="forWork" inputId="for_work" :binary="true" />
            <label for="for_work" class="cursor-pointer">Работы</label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="forContract" inputId="for_contract" :binary="true" />
            <label for="for_contract" class="cursor-pointer">Договоры</label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="forObject" inputId="for_object" :binary="true" />
            <label for="for_object" class="cursor-pointer">Объекты</label>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Отмена"
          severity="secondary"
          @click="handleClose"
          rounded
          size="small"
          :disabled="loading"
        />
        <Button
          label="Сохранить"
          severity="info"
          @click="handleSave"
          rounded
          size="small"
          :disabled="loading"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
